import React, { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { LayoutDashboard, Users, Radar, Activity, LogOut, ShieldCheck, Megaphone } from "lucide-react";
import basiqMarkWhite from "../../assets/basiq-mark-white.png";
import basiqMark from "../../assets/basiq-mark.png";
import { adminMe, adminLogout } from "../../lib/adminApi";
import { useAdminTheme } from "../../lib/adminTheme";
import { ThemeToggle } from "./ui";
import AdminLogin from "./AdminLogin";
import AdminOverview from "./AdminOverview";
import AdminUsers from "./AdminUsers";
import AdminLeads from "./AdminLeads";
import AdminActivity from "./AdminActivity";
import AdminMarketing from "./AdminMarketing";

const TABS = [
  { id: "overview", label: "Visão geral", icon: LayoutDashboard },
  { id: "users", label: "Usuários & membros", icon: Users },
  { id: "leads", label: "Leads", icon: Radar },
  { id: "marketing", label: "Marketing", icon: Megaphone },
  { id: "activity", label: "Atividade", icon: Activity },
];

export default function AdminApp() {
  const [admin, setAdmin] = useState(null); // null=checking, false=login, {}=ok
  const [tab, setTab] = useState("overview");
  const { isLight, toggle } = useAdminTheme();
  const mark = isLight ? basiqMark : basiqMarkWhite;

  useEffect(() => {
    document.title = "Basiq Admin";
    adminMe().then(setAdmin).catch(() => setAdmin(false));
  }, []);

  const logout = async () => { try { await adminLogout(); } finally { setAdmin(false); } };

  if (admin === null) return <div className="min-h-screen bg-[#0B0F19]" />;
  if (admin === false) return <><AdminLogin onAuthed={setAdmin} /><Toaster position="top-center" theme={isLight ? "light" : "dark"} /></>;

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 flex" data-testid="admin-shell">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/[0.06] bg-[#0D1220] px-4 py-6 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-2">
          <img src={mark} alt="" className="h-6 w-auto" />
          <div>
            <div className="text-[14px] font-bold leading-none">Basiq</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#7DD3FC] mt-1">Admin</div>
          </div>
        </div>
        <nav className="mt-8 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 h-10 rounded-xl text-[13px] transition-colors ${tab === t.id ? "bg-[#38BDF8]/20 text-white ring-1 ring-[#38BDF8]/40" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto space-y-3">
          <ThemeToggle isLight={isLight} onToggle={toggle} className="w-full justify-center" />
          <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/[0.06] p-3">
            <div className="flex items-center gap-2 text-[12px] text-slate-300 break-all"><ShieldCheck size={14} className="text-emerald-400 shrink-0" /> {admin.email}</div>
            <button onClick={logout} data-testid="admin-logout" className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-slate-400 hover:text-white"><LogOut size={13} /> Sair</button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-20 bg-[#0D1220]/90 backdrop-blur border-b border-white/[0.06] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><img src={mark} alt="" className="h-5 w-auto" /><span className="text-[13px] font-bold">Admin</span></div>
          <div className="flex items-center gap-2">
            <ThemeToggle isLight={isLight} onToggle={toggle} className="h-8" />
            <button onClick={logout} className="text-[12px] text-slate-400" data-testid="admin-logout-mobile">Sair</button>
          </div>
        </header>
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-white/[0.06]">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`shrink-0 px-3 h-8 rounded-full text-[12px] ${tab === t.id ? "bg-[#38BDF8] text-white" : "text-slate-400 bg-white/5"}`}>{t.label}</button>
          ))}
        </div>
        <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1400px]">
          {tab === "overview" && <AdminOverview />}
          {tab === "users" && <AdminUsers />}
          {tab === "leads" && <AdminLeads />}
          {tab === "marketing" && <AdminMarketing />}
          {tab === "activity" && <AdminActivity />}
        </div>
      </main>
      <Toaster position="top-center" theme={isLight ? "light" : "dark"} />
    </div>
  );
}
